import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import PhoneInput from '../components/PhoneInput';
import { usePhoneValidation, usePhoneApiError } from '../hooks/usePhoneValidation';

// Feature flag to control default authentication method
const USE_EMAIL_ONLY = import.meta.env.VITE_USE_EMAIL_ONLY === 'true';

const RegisterPage: React.FC = () => {
  const { passwordlessRegister, verifyOTP, resendOTP, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState<'register' | 'otp'>('register');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    userType: 'client' as 'client' | 'provider',
  });
  const [otp, setOtp] = useState('');
  const [session, setSession] = useState<string>('');
  const [identifierType, setIdentifierType] = useState<'phone' | 'email'>(USE_EMAIL_ONLY ? 'email' : 'phone');
  
  // Phone validation hooks
  const phoneValidation = usePhoneValidation({
    onApiError: (error) => {
      // This will be handled by the API error handling
    }
  });
  const { apiError: phoneApiError, handleApiError, clearError } = usePhoneApiError();

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Register submit triggered');
    console.log('Form data:', formData);
    console.log('Identifier type:', identifierType);
    console.log('USE_EMAIL_ONLY:', USE_EMAIL_ONLY);
    console.log('Phone validation:', phoneValidation);
    
    // Validate based on identifier type
    if (!formData.name.trim()) {
      console.log('Name validation failed');
      return;
    }
    
    if (identifierType === 'phone' && !phoneValidation.isPhoneValid) {
      console.log('Phone validation failed');
      return;
    }
    
    if (identifierType === 'email' && !formData.email.trim()) {
      console.log('Email validation failed');
      return;
    }

    console.log('All validations passed, making API call');

    // Clear any previous API errors
    clearError();

    const registerData = {
      name: formData.name.trim(),
      userType: formData.userType,
      ...(identifierType === 'email' 
        ? { email: formData.email.trim() }
        : { phone: phoneValidation.formattedPhone, email: formData.email.trim() || undefined }
      )
    };

    console.log('Register data:', registerData);

    try {
      const result = await passwordlessRegister(registerData);
      console.log('Registration result:', result);
      
      if (result.success && result.session) {
        setSession(result.session);
        setStep('otp');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      // Handle phone-specific API errors
      const errorMessage = handleApiError(error);
      if (errorMessage.includes('phone') || errorMessage.includes('número') || errorMessage.includes('teléfono')) {
        phoneValidation.setApiError(errorMessage);
      }
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp.trim()) {
      return;
    }

    const verifyData = {
      code: otp.trim(),
      session,
      ...(identifierType === 'email' ? { email: formData.email } : { phone: formData.phone })
    };

    await verifyOTP(verifyData);
  };

  const handleResendOTP = async () => {
    const registerData = {
      name: formData.name,
      userType: formData.userType,
      ...(identifierType === 'email' 
        ? { email: formData.email.trim() }
        : { phone: phoneValidation.formattedPhone, email: formData.email.trim() || undefined }
      )
    };

    await passwordlessRegister(registerData);
  };

  const handleBackToRegister = () => {
    setStep('register');
    setOtp('');
    setSession('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (value: string, validation: any) => {
    phoneValidation.setPhoneNumber(value);
    setFormData({
      ...formData,
      phone: value,
    });
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const field = e.target.name;
    
    setFormData({
      ...formData,
      [field]: value,
    });
    
    // Auto-detect if it's a phone number or email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    
    if (field === 'email' && emailRegex.test(value)) {
      setIdentifierType('email');
    } else if (field === 'phone' && phoneRegex.test(value) && value.length >= 10) {
      setIdentifierType('phone');
    }
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === 'register' ? t('createYourAccount') : t('verifyCode')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'register' ? t('joinSimpleHandshake') : t('enterVerificationCode')}
          </p>
        </div>
        
        {step === 'register' ? (
          <form className="mt-8 space-y-6" onSubmit={handleRegisterSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  {t('fullName')} *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className="input-field mt-1"
                  placeholder={t('enterFullName')}
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              {identifierType === 'email' ? (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('emailAddress')} *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="input-field mt-1"
                    placeholder={t('enterEmail')}
                    value={formData.email}
                    onChange={handleIdentifierChange}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('primaryLoginMethod')}
                  </p>
                </div>
              ) : (
                <div>
                  <PhoneInput
                    value={phoneValidation.phoneNumber}
                    onChange={handlePhoneChange}
                    label={t('phoneNumber')}
                    required
                    error={phoneApiError || undefined}
                    id="phone"
                    name="phone"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('primaryLoginMethod')}
                  </p>
                </div>
              )}
              
              {!USE_EMAIL_ONLY && identifierType === 'phone' && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('emailAddress')} ({t('optional')})
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="input-field mt-1"
                    placeholder={t('enterEmail')}
                    value={formData.email}
                    onChange={handleIdentifierChange}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('emailForMarketingAndBackup')}
                  </p>
                </div>
              )}
              
              {!USE_EMAIL_ONLY && identifierType === 'email' && (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    {t('phoneNumber')} ({t('optional')})
                  </label>
                  <PhoneInput
                    value={phoneValidation.phoneNumber}
                    onChange={handlePhoneChange}
                    label=""
                    required={false}
                    error={phoneApiError || undefined}
                    id="phone"
                    name="phone"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('phoneForBackupAndNotifications')}
                  </p>
                </div>
              )}
              
              <div>
                <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                  {t('iAmA')} *
                </label>
                <select
                  id="userType"
                  name="userType"
                  required
                  className="input-field mt-1"
                  value={formData.userType}
                  onChange={handleChange}
                >
                  <option value="client">{t('client')}</option>
                  <option value="provider">{t('provider')}</option>
                </select>
              </div>
              
              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                <p className="font-medium">{t('howItWorks')}:</p>
                <ul className="mt-1 space-y-1">
                  <li>• {t('smsPrimaryMethod')}</li>
                  <li>• {t('emailForBackup')}</li>
                  <li>• {t('secureOTPVerification')}</li>
                </ul>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={
                  isLoading || 
                  !formData.name.trim() || 
                  (identifierType === 'phone' && !phoneValidation.isPhoneValid) ||
                  (identifierType === 'email' && !formData.email.trim())
                }
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {isLoading ? t('sendingCode') : t('sendCode')}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('alreadyHaveAccount')}{' '}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  {t('signInHere')}
                </Link>
              </p>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleOTPSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  {t('verificationCode')}
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  className="input-field mt-1 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('codeSentTo')} {identifierType === 'email' ? formData.email : formData.phone}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {isLoading ? t('verifying') : t('verifyCode')}
              </button>
              
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="w-full text-sm text-blue-600 hover:text-blue-500 disabled:text-gray-400"
              >
                {t('resendCode')}
              </button>
              
              <button
                type="button"
                onClick={handleBackToRegister}
                disabled={isLoading}
                className="w-full text-sm text-gray-600 hover:text-gray-500 disabled:text-gray-400"
              >
                {t('backToRegister')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterPage; 